import React from 'react';
import Icon from '../AppIcon';

const LoadingIndicator = ({ 
  type = 'spinner', 
  size = 'default', 
  message = 'Loading...', 
  progress = null,
  showMessage = true,
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    default: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  const Spinner = () => (
    <div className={`${sizeClasses?.[size]} animate-spin`}>
      <Icon name="Loader2" size={size === 'sm' ? 16 : size === 'lg' ? 32 : size === 'xl' ? 48 : 24} />
    </div>
  );

  const ProgressBar = () => (
    <div className="w-full bg-muted rounded-full h-2">
      <div 
        className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
        style={{ width: `${Math.min(Math.max(progress || 0, 0), 100)}%` }}
      />
    </div>
  );

  const Skeleton = () => (
    <div className="animate-pulse space-y-3">
      <div className="h-4 bg-muted rounded w-3/4"></div>
      <div className="h-4 bg-muted rounded w-1/2"></div>
      <div className="h-4 bg-muted rounded w-5/6"></div>
    </div>
  );

  const Shimmer = () => (
    <div className="relative overflow-hidden bg-muted rounded-lg h-32">
      <div 
        className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
          backgroundSize: '200% 100%'
        }}
      />
    </div>
  );

  const Dots = () => (
    <div className="flex space-x-1">
      {[0, 1, 2]?.map((i) => (
        <div
          key={i}
          className={`${sizeClasses?.[size]} bg-primary rounded-full animate-pulse`}
          style={{
            animationDelay: `${i * 0.2}s`,
            animationDuration: '1s'
          }}
        />
      ))}
    </div>
  );

  const renderLoader = () => {
    switch (type) {
      case 'progress':
        return <ProgressBar />;
      case 'skeleton':
        return <Skeleton />;
      case 'shimmer':
        return <Shimmer />;
      case 'dots':
        return <Dots />;
      default:
        return <Spinner />;
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center space-y-3 ${className}`}>
      {renderLoader()}
      
      {showMessage && message && type !== 'skeleton' && type !== 'shimmer' && (
        <div className="text-center">
          <p className="text-sm text-muted-foreground">{message}</p>
          {progress !== null && type === 'progress' && (
            <p className="text-xs text-muted-foreground mt-1">
              {Math.round(progress)}% complete
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Global loading overlay component
export const LoadingOverlay = ({ 
  isVisible = false, 
  message = 'Processing...', 
  progress = null,
  onCancel = null 
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-loading flex items-center justify-center">
      <div className="bg-card border border-border rounded-lg p-8 shadow-elevation-3 max-w-sm w-full mx-4">
        <LoadingIndicator 
          type={progress !== null ? 'progress' : 'spinner'}
          size="lg"
          message={message}
          progress={progress}
        />
        
        {onCancel && (
          <button
            onClick={onCancel}
            className="mt-4 w-full px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-micro"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

// Inline loading states for buttons and form elements
export const InlineLoader = ({ size = 'sm', className = '' }) => (
  <LoadingIndicator 
    type="spinner" 
    size={size} 
    showMessage={false} 
    className={className}
  />
);

export default LoadingIndicator;